import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClub } from '../../app/club';
import { useAccessEvents, useAccessRules, useClubAudit, useDoors, useGroupMembers, usePersons } from '../../data/hooks';
import { Badge, Button, EmptyState, ErrorState, Field, Loading, PageHeader, Panel } from '../../components/ui';
import { downloadCsv, fmtWeekdayDateTime } from '../../lib/format';
import type { AuditCategory, EventMethod, EventResult } from '../../lib/types';
import { AUDIT_CATEGORY_LABEL, describeAudit } from '../../lib/auditText';
import { computeScope, inScope } from '../../app/scope';

export const EVENT_METHOD_LABEL: Record<EventMethod, string> = {
  app: 'App',
  card: 'Pas',
  fingerprint: 'Vingerafdruk',
  pin: 'Pincode',
  temp_code: 'Tijdelijke code',
  mechanical_key: 'Sleutel',
  remote: 'Op afstand',
  unknown: 'Onbekend',
};

const RESULT_LABEL: Record<EventResult, string> = { granted: 'Open', denied: 'Geweigerd', alarm: 'Alarm' };
const PAGE = 50;

/**
 * Logboek met twee weergaven: wie deuren opende (access_events) en "Wijzigingen" (beheerlogboek uit audit_log via
 * RPC club_audit_log). Wijzigingen zijn alleen voor clubbeheerders en Antreja; een groepsbeheerder ziet dat tabblad
 * niet (de database weigert het ook).
 */
export function LogbookPage() {
  const { role } = useClub();
  const [params, setParams] = useSearchParams();
  const canChanges = role === 'club_admin' || role === 'platform_admin' || role === 'support';
  const view = canChanges && params.get('weergave') === 'wijzigingen' ? 'changes' : 'events';
  const go = (v: 'events' | 'changes') => {
    const p = new URLSearchParams(params);
    if (v === 'changes') p.set('weergave', 'wijzigingen');
    else p.delete('weergave');
    setParams(p, { replace: true });
  };
  const tabs = canChanges ? (
    <div className="tabs" role="tablist" aria-label="Logboek" style={{ margin: 0 }}>
      <button role="tab" aria-selected={view === 'events'} onClick={() => go('events')}>
        Deuren geopend
      </button>
      <button role="tab" aria-selected={view === 'changes'} onClick={() => go('changes')}>
        Wijzigingen
      </button>
    </div>
  ) : null;
  return view === 'changes' ? <ChangesView tabs={tabs} /> : <EventsView tabs={tabs} />;
}

function EventsView({ tabs }: { tabs: ReactNode }) {
  const { orgId, role, managedGroupIds, org } = useClub();
  const [days, setDays] = useState(7);
  const [doorId, setDoorId] = useState('');
  const [result, setResult] = useState<EventResult | ''>('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const since = useMemo(() => new Date(Date.now() - days * 86400_000).toISOString(), [days]);
  const events = useAccessEvents(orgId, { since, door_id: doorId || undefined, result: result || undefined });
  const doors = useDoors(orgId);
  const persons = usePersons(orgId);
  const members = useGroupMembers(orgId);
  const rules = useAccessRules(orgId);

  const qs = [events, doors, persons, members, rules];
  if (qs.some((q) => q.isLoading)) return <Loading />;
  const err = qs.find((q) => q.error)?.error;
  if (err) return <ErrorState error={err} />;

  const scope = computeScope(role, managedGroupIds, members.data!, rules.data!);
  const doorName = new Map(doors.data!.map((d) => [d.id, d.name]));
  const personName = new Map(persons.data!.map((p) => [p.id, p.full_name]));
  const q = query.trim().toLowerCase();
  const rows = events.data!.filter(
    (e) =>
      // group_manager: only own group members on own group doors (spec 02 note 8)
      (!scope || (e.person_id && inScope(scope, 'personIds', e.person_id) && e.door_id && inScope(scope, 'doorIds', e.door_id))) &&
      (!q || (e.person_id && personName.get(e.person_id)?.toLowerCase().includes(q))),
  );
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const visible = rows.slice(page * PAGE, page * PAGE + PAGE);
  const visibleDoors = doors.data!.filter((d) => inScope(scope, 'doorIds', d.id));

  return (
    <div className="stack-lg">
      <PageHeader
        title="Logboek"
        intro={`Events komen binnen bij de volgende app-verbinding met het slot. Bewaartermijn: ${org.log_retention_days} dagen.`}
        actions={
          <Button
            onClick={() =>
              downloadCsv(
                `logboek-${new Date().toISOString().slice(0, 10)}.csv`,
                ['Tijd', 'Deur', 'Persoon', 'Methode', 'Resultaat'],
                rows.map((e) => [
                  e.occurred_at,
                  e.door_id ? doorName.get(e.door_id) ?? '' : '',
                  e.person_id ? personName.get(e.person_id) ?? '' : '',
                  EVENT_METHOD_LABEL[e.method],
                  RESULT_LABEL[e.result],
                ]),
              )
            }
            disabled={rows.length === 0}
          >
            Exporteer CSV
          </Button>
        }
      />
      {tabs}
      <Panel>
        <div className="toolbar">
          <Field label="Periode">
            {(p) => (
              <select {...p} className="input" value={days} onChange={(e) => { setDays(Number(e.target.value)); setPage(0); }}>
                <option value={1}>Afgelopen 24 uur</option>
                <option value={7}>Afgelopen week</option>
                <option value={30}>Afgelopen 30 dagen</option>
                <option value={90}>Afgelopen 90 dagen</option>
              </select>
            )}
          </Field>
          <Field label="Deur">
            {(p) => (
              <select {...p} className="input" value={doorId} onChange={(e) => { setDoorId(e.target.value); setPage(0); }}>
                <option value="">Alle deuren</option>
                {visibleDoors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Resultaat">
            {(p) => (
              <select {...p} className="input" value={result} onChange={(e) => { setResult(e.target.value as EventResult | ''); setPage(0); }}>
                <option value="">Alles</option>
                <option value="granted">Open</option>
                <option value="denied">Geweigerd</option>
                <option value="alarm">Alarm</option>
              </select>
            )}
          </Field>
          <div className="grow">
            <Field label="Zoek persoon">
              {(p) => <input {...p} className="input" type="search" value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} />}
            </Field>
          </div>
        </div>
        {rows.length === 0 ? (
          <EmptyState title="Geen events gevonden">Pas de filters aan of kies een langere periode.</EmptyState>
        ) : (
          <>
            <div className="table-wrap">
              <table className="data stack-mobile">
                <thead>
                  <tr>
                    <th scope="col">Tijd</th>
                    <th scope="col">Deur</th>
                    <th scope="col">Persoon</th>
                    <th scope="col">Methode</th>
                    <th scope="col">Resultaat</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((e) => (
                    <tr key={e.id}>
                      <td className="num cell-main">{fmtWeekdayDateTime(e.occurred_at)}</td>
                      <td data-label="Deur">{e.door_id ? doorName.get(e.door_id) : '—'}</td>
                      <td data-label="Persoon">{e.person_id ? personName.get(e.person_id) ?? 'Onbekend' : <span className="muted">Code / gast</span>}</td>
                      <td data-label="Methode">{EVENT_METHOD_LABEL[e.method]}</td>
                      <td>
                        <Badge tone={e.result === 'granted' ? 'green' : 'red'}>{RESULT_LABEL[e.result]}</Badge>
                        {e.source === 'app_reported' && (
                          <span className="muted" title="Aangeleverd door een app; niet bevestigd door het slot of de cloud">
                            {' '}· via app (onbevestigd)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={page} pages={pages} total={rows.length} onChange={setPage} />
          </>
        )}
      </Panel>
    </div>
  );
}

export function Pager({ page, pages, total, onChange }: { page: number; pages: number; total: number; onChange: (p: number) => void }) {
  if (pages <= 1) return <p className="muted small" style={{ padding: '12px 20px' }}>{total} resultaten</p>;
  return (
    <nav className="row" aria-label="Paginering" style={{ padding: '12px 20px', justifyContent: 'space-between' }}>
      <span className="muted small">
        Pagina {page + 1} van {pages}, {total} resultaten
      </span>
      <span className="row">
        <Button size="sm" disabled={page === 0} onClick={() => onChange(page - 1)}>
          Vorige
        </Button>
        <Button size="sm" disabled={page >= pages - 1} onClick={() => onChange(page + 1)}>
          Volgende
        </Button>
      </span>
    </nav>
  );
}

const AUDIT_PAGE = 50;
const CATEGORIES = Object.keys(AUDIT_CATEGORY_LABEL) as AuditCategory[];

/** Beheerlogboek: wie wat wanneer heeft gewijzigd, in gewone zinnen. Server-side gefilterd en gepagineerd. */
function ChangesView({ tabs }: { tabs: ReactNode }) {
  const { orgId } = useClub();
  const [days, setDays] = useState(30);
  const [category, setCategory] = useState<AuditCategory | ''>('');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  // Zoeken pas na een korte pauze in het typen.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);
  const from = useMemo(() => (days > 0 ? new Date(Date.now() - days * 86400_000).toISOString() : null), [days]);
  const audit = useClubAudit(orgId, {
    from,
    to: null,
    category: category || null,
    search: search || null,
    limit: AUDIT_PAGE,
    offset: page * AUDIT_PAGE,
  });
  const total = audit.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / AUDIT_PAGE));

  return (
    <div className="stack-lg">
      <PageHeader title="Logboek" intro="Wijzigingen in het beheer van de club: wie wat heeft aangemaakt, gewijzigd of ingetrokken, en wanneer." />
      {tabs}
      <Panel>
        <div className="toolbar">
          <Field label="Periode">
            {(p) => (
              <select {...p} className="input" value={days} onChange={(e) => { setDays(Number(e.target.value)); setPage(0); }}>
                <option value={7}>Afgelopen week</option>
                <option value={30}>Afgelopen 30 dagen</option>
                <option value={90}>Afgelopen 90 dagen</option>
                <option value={365}>Afgelopen jaar</option>
                <option value={0}>Alles</option>
              </select>
            )}
          </Field>
          <Field label="Soort">
            {(p) => (
              <select {...p} className="input" value={category} onChange={(e) => { setCategory(e.target.value as AuditCategory | ''); setPage(0); }}>
                <option value="">Alles</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {AUDIT_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <div className="grow">
            <Field label="Zoeken" hint="Naam van een persoon, deur, gebouw, groep of beheerder">
              {(p) => <input {...p} className="input" type="search" value={query} onChange={(e) => setQuery(e.target.value)} />}
            </Field>
          </div>
        </div>
        {audit.isLoading ? (
          <Loading />
        ) : audit.error ? (
          <ErrorState error={audit.error} />
        ) : audit.data!.rows.length === 0 ? (
          <EmptyState title="Geen wijzigingen gevonden">Pas de filters aan of kies een langere periode.</EmptyState>
        ) : (
          <>
            <ul className="audit-list" aria-label="Wijzigingen">
              {audit.data!.rows.map((r) => (
                <li key={r.id}>
                  <time dateTime={r.created_at}>{fmtWeekdayDateTime(r.created_at)}</time>
                  <span>{describeAudit(r)}</span>
                  <Badge>{AUDIT_CATEGORY_LABEL[r.category] ?? 'Overig'}</Badge>
                </li>
              ))}
            </ul>
            <Pager page={page} pages={pages} total={total} onChange={setPage} />
          </>
        )}
      </Panel>
    </div>
  );
}
